import { useEffect, useMemo, useState } from 'react';
import '@amzn/dcv-web-sdk/workspaces.css';
import { WebLogUploader } from '@amzn/euc-web-log-uploader';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@/stores/session';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import { InSessionFactory } from '@/bridge/factory/InSessionFactory';
import { WorkspacesViewer } from '@amzn/dcv-web-sdk/workspaces';
import ConnectStatus from '@/components/ConnectStatus';
import { preloadAgenticViewer } from '@/components/AgenticRoute/agenticViewerChunk';
import { useLogUploaderPreferenceStore } from '@stores/loguploader';
import {
  buildDcvInSessionMetricsConfigInput,
  buildWebDcvInSessionConfigInput,
  persistWspContext,
} from '@views/Stream/utils';
import { makeViewerMetricsAdapter } from '@/bridge/metrics/viewerMetricsAdapter';
import {
  useConnectionSettingsStore,
  Localization,
} from '@amzn/work-spaces-web-client-core-lib';
import { useReportHeartbeatTask } from '@/hooks/useReportHeartbeatTask';
import { isIosDevice, isMobileDevice } from '@/utils/browser';
import { isPWA } from '@/utils/pwa';
import { isAgenticWorkspace } from '@views/Stream/agentic';
import {
  handleHeartbeatError,
  inSessionDisconnectUrl,
} from '@views/Stream/heartbeatError';

interface StreamingProps {}

const logger = CoreFactory.getLogger();

type DcvLogLevel = 0 | 1 | 2 | 3 | 4 | 5;
const DCV_LOG_LEVEL: Record<string, DcvLogLevel> = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  SILENT: 5,
};

const toDcvLogLevel = (level?: string): DcvLogLevel =>
  DCV_LOG_LEVEL[(level ?? '').toUpperCase()] ?? DCV_LOG_LEVEL.INFO;

const metrics = CoreFactory.getMetrics();
const viewerMetricsPublisher = makeViewerMetricsAdapter(metrics, logger);
const isPwaAtStartup = isPWA();

const IN_SESSION_VIEWPORT =
  'width=device-width, initial-scale=1.0, user-scalable=no, interactive-widget=overlays-content, viewport-fit=cover';

interface InSessionParams {
  authToken: string;
  extensionData: string | undefined;
  logUploader: WebLogUploader | undefined;
  extendedServerEndpoints: string | undefined;
}

const Maxibon: React.FC<StreamingProps> = () => {
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        logger.info(
          'Page rendered from b/f cache, forcing reload to establish connection'
        );
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    // Disable page zoom/pan, text selection and keyboard-driven viewport
    // resizes outside the canvas (toolbar, letterbox, modals).
    const viewportMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]'
    );
    const previousViewport = viewportMeta?.getAttribute('content');
    viewportMeta?.setAttribute('content', IN_SESSION_VIEWPORT);

    const bodyStyle = document.body.style;
    const previousBodyStyle = {
      touchAction: bodyStyle.touchAction,
      userSelect: bodyStyle.userSelect,
      webkitUserSelect: bodyStyle.webkitUserSelect,
    };
    bodyStyle.touchAction = 'none';
    bodyStyle.userSelect = 'none';
    bodyStyle.webkitUserSelect = 'none';

    const preventDefaultHandler = (event: Event) => event.preventDefault();
    const isIos = isIosDevice();
    const isIosMobile = isIos && isMobileDevice();
    const { audioSession } = window.navigator;
    const previousAudioSessionType = audioSession?.type;

    /**
     * iOS devices enable gestures even if touch-action: none in html.
     * Adding event preventDefault to prevent gesture page zoom when modals are open.
     */
    if (isIos) {
      window.addEventListener('gesturestart', preventDefaultHandler);
      window.addEventListener('gesturechange', preventDefaultHandler);
      window.addEventListener('gestureend', preventDefaultHandler);

      if (isIosMobile) {
        // Add padding to avoid windows in-session toolbar clamping due to round corners
        document.documentElement.classList.add('safe-area-insets');
        // Play audio also if iPhone device is in silent mode
        if (audioSession) {
          audioSession.type = 'play-and-record';
        }
      }
    }

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      if (viewportMeta && previousViewport != null) {
        viewportMeta.setAttribute('content', previousViewport);
      }
      Object.assign(bodyStyle, previousBodyStyle);
      if (isIos) {
        window.removeEventListener('gesturestart', preventDefaultHandler);
        window.removeEventListener('gesturechange', preventDefaultHandler);
        window.removeEventListener('gestureend', preventDefaultHandler);
        if (isIosMobile) {
          document.documentElement.classList.remove('safe-area-insets');
          if (audioSession && previousAudioSessionType !== undefined) {
            audioSession.type = previousAudioSessionType;
          }
        }
      }
    };
  }, []);

  const [sessionParams, setSessionParams] = useState<InSessionParams | null>(
    null
  );
  const sessionTimedMetricSummary = useMemo(
    () => buildDcvInSessionMetricsConfigInput().sessionTimedMetricSummary,
    []
  );
  const redirectPort = window.location.port ? `:${window.location.port}` : '';
  const returnUrl = inSessionDisconnectUrl();
  const sessionContext = useSessionStore(
    (state) => state.resourceSessionContext
  );
  const resourceId = useSessionStore((state) => state.resourceId);
  const resourceName = useSessionStore((state) => state.resourceName);
  const resourceComputeType = useSessionStore(
    (state) => state.resourceComputeType
  );
  const resourceIp = useSessionStore((state) => state.resourceIp);
  const resourceIpv6 = useSessionStore((state) => state.resourceIpv6);
  const resource = useSessionStore((state) => state.resource);
  const regCode = useSessionStore((state) => state.registrationCode);
  const isLogUploaderEnabledAtDirectoryLevel = useSessionStore(
    (state) => state.isLogUploaderEnabledAtDirectoryLevel
  );

  const isLogUploaderEnabledByUser = useLogUploaderPreferenceStore(
    (state) => state.isLoggingEnabledByUser
  );
  const logUploaderLogLevel = useLogUploaderPreferenceStore(
    (state) => state.logLevelPreference
  );
  const isUseDualStackIPv6 = useConnectionSettingsStore(
    (state) => state.useDualStackIPv6
  );
  const navigate = useNavigate();

  const { startReportingHeartbeat } = useReportHeartbeatTask({
    onError: handleHeartbeatError,
  });

  const hasRequiredSessionFields = Boolean(
    sessionContext?.SessionId &&
      sessionContext?.AuthToken &&
      sessionContext?.ServerUrl
  );

  useEffect(() => {
    // Wait until the resource session context is populated before starting the
    // viewer; the dcv config below reads SessionId/AuthToken/ServerUrl from it.
    if (!hasRequiredSessionFields) {
      return;
    }

    const wspContext = buildWebDcvInSessionConfigInput(
      isLogUploaderEnabledByUser,
      isLogUploaderEnabledAtDirectoryLevel,
      logUploaderLogLevel,
      isUseDualStackIPv6
    );

    logger.info('Starting WSP session streaming');

    setInSessionUtilityParams(
      redirectPort,
      resourceId,
      resourceName,
      resourceComputeType,
      resourceIp,
      resourceIpv6,
      regCode as string
    );

    const { authToken, extensionData, extendedServerEndpoints } =
      persistWspContext(wspContext);

    if (isAgenticWorkspace(resource?.ResourceDetails)) {
      // The viewer at /agentic reads the wsp_context handoff written above.
      logger.info('Routing to in-realm agentic viewer');
      preloadAgenticViewer();
      navigate('/agentic');
      return;
    }

    setSessionParams({
      authToken: authToken ?? sessionContext.AuthToken,
      extensionData: extensionData ?? sessionContext.ExtensionData,
      extendedServerEndpoints:
        extendedServerEndpoints ?? sessionContext.ExtendedServerUrls,
      logUploader: InSessionFactory.getWspLogUploader({
        isLogUploaderEnabledByUser,
        isLogUploaderEnabledAtDirectoryLevel,
        useDualStackIpv6: isUseDualStackIPv6,
      }),
    });

    startReportingHeartbeat();
  }, [hasRequiredSessionFields]);

  return sessionParams && hasRequiredSessionFields ? (
    // The viewer canvas fills its container, so give it an explicit full-height
    // wrapper (the old flow rendered in a bare full-page wsp.html document).
    <div style={{ height: '100%', width: '100%' }}>
      <WorkspacesViewer
        dcv={{
          sessionId: sessionContext.SessionId,
          authToken: sessionParams.authToken,
          serverUrl: sessionContext.ServerUrl,
          extendedServerEndpoints: sessionParams.extendedServerEndpoints,
          extensionData: sessionParams.extensionData,
          // Origin-absolute so the DCV core's worker bootstrap (blob worker +
          // importScripts) resolves assets against the origin, not the current
          // route (/stream), which would 404 as /stream/static/dcvjs/...
          baseUrl: `${window.location.origin}/static/dcvjs/`, // matches scripts/copy-dcv-assets.sh dest
          logLevel: toDcvLogLevel(logUploaderLogLevel),
          isPWA: isPwaAtStartup,
          returnUrl,
          observability: {
            logUploader: sessionParams.logUploader,
            metricsPublisher: viewerMetricsPublisher,
            sessionTimedMetricSummary,
          },
        }}
        uiConfig={{ locale: Localization.getCurrentLocale() }}
      />
    </div>
  ) : (
    <ConnectStatus />
  );
};

/*
 This function sets the workspace details in storage. This will be used in session for the toolbar: 'About My Workspace'
*/
const setInSessionUtilityParams = (
  port: string,
  id: string,
  name: string,
  type: string,
  ip: string,
  ipv6: string,
  regCode: string
) => {
  window.sessionStorage.setItem(
    'return_url',
    `https://${window.location.hostname}${port}/disconnect`
  );
  window.localStorage.setItem('workspaceId', id);
  window.localStorage.setItem('workspaceName', name);
  window.localStorage.setItem('workspaceComputeType', type);
  window.localStorage.setItem('workspaceIp', ip);
  window.localStorage.setItem('workspaceIpv6', ipv6);
  window.localStorage.setItem('registrationCode', regCode);
};

export default Maxibon;
